import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2 } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState } from "@/components/LoadingState";
import { staffViewsApi } from "@/api/endpoints";

interface BldgRow {
  id: string;
  name: string;
  city: string;
  organisation_id: string;
  organisation_name: string;
  unit_count: number;
  meter_count: number;
  open_alert_count: number;
}

export default function StaffBuildingsPage() {
  const [rows, setRows] = useState<BldgRow[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    staffViewsApi.buildings().then((r) => { setRows(r); setLoading(false); });
  }, []);

  const totalUnits = rows.reduce((s, r) => s + r.unit_count, 0);
  const totalMeters = rows.reduce((s, r) => s + r.meter_count, 0);
  const totalAlerts = rows.reduce((s, r) => s + r.open_alert_count, 0);

  const columns: Column<BldgRow>[] = [
    {
      key: "name", header: "Name",
      render: (b) => (
        <Link
          to={`/staff/organisations/${b.organisation_id}/buildings/${b.id}`}
          className="text-brand-700 font-medium hover:underline"
        >
          {b.name}
        </Link>
      ),
    },
    { key: "city", header: "City", render: (b) => b.city },
    {
      key: "org", header: "Organisation",
      render: (b) => (
        <Link to={`/staff/organisations/${b.organisation_id}`} className="text-slate-700 hover:underline">
          {b.organisation_name}
        </Link>
      ),
    },
    { key: "units", header: "Units", render: (b) => b.unit_count },
    { key: "meters", header: "Meters", render: (b) => b.meter_count },
    {
      key: "alerts", header: "Open alerts",
      render: (b) => b.open_alert_count > 0
        ? <span className="badge bg-red-50 text-red-700">{b.open_alert_count}</span>
        : <span className="text-slate-400">0</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader title="Buildings across all organisations" subtitle="Click any building to manage it" />
      {loading ? <LoadingState /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiCard label="Total buildings" value={rows.length} icon={<Building2 size={18} />} />
            <KpiCard label="Total units" value={totalUnits} accent="blue" />
            <KpiCard label="Total meters" value={totalMeters} accent="slate" />
            <KpiCard label="Open alerts" value={totalAlerts} accent={totalAlerts ? "red" : "brand"} />
          </div>
          <DataTable columns={columns} rows={rows} emptyMessage="No buildings yet" />
        </>
      )}
    </AppLayout>
  );
}
